import * as vp from 'vopay';
import { Box, Table, withStyles } from 'vopay';
import { Button, Title } from 'UI';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';
import useResetPassword from '../../../hooks/useApi/useResetPassword';
import styles from './UsersList.module.scss';
import PersonAddIcon from './person_add_alt.svg';

const cn = withStyles(styles);

type AccountUserType = {
  Username: string;
  'Date Added'?: string;
  UserType: string;
};

type UsersListProps = {
  bearer: string;
  accounts: AccountUserType[];
  handlers: {
    onAddUserPage: () => void;
    onDeleteUser: (values: vp.DeleteAccountUserRequest) => Promise<void>;
    onAccountUserPermissionPage: (values: vp.AccountUserPermissionsRequest) => void;
  };
};

type FC = FunctionComponent<UsersListProps>;

const Users: FC = (props: UsersListProps) => {
  const { handlers, accounts, bearer } = props;
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const { resetPassword } = useResetPassword(bearer);
  const { t } = useTranslation('translations', { keyPrefix: 'Users.AccountUsersList' });

  const handleResetPasswordDialog = async (userEmail: string) => {
    const result = await alertDialog.open(t('resetPassword.dialogTitle'), t('resetPassword.dialogContent'), {
      abortLabel: t('resetPassword.cancel'),
      confirmLabel: t('resetPassword.sendResetEmail'),
    });

    if (result === 'confirm') {
      try {
        await resetPassword({ Username: userEmail });
        toast.success(t('resetPassword.success'));
      } catch (err) {
        console.error(err);
        toast.neutral(t('resetPassword.error'));
      }
    }
  };

  const tableProps: vp.TableProps<AccountUserType> = {
    id: 'user-account-list-table',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'Username',
        header: t('table.emailAddress'),
        align: 'left',
        field: 'Username',
      },
      {
        id: 'DateAdded',
        header: t('table.dateAdded'),
        align: 'left',
        field: 'Date Added',
      },
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data }) => {
          return (
            <Box jc="fe" gap="sm">
              <Button
                id={`user-change-Permissions-${data.Username}`}
                onClick={() => {
                  handlers.onAccountUserPermissionPage({ EmailAddress: data.Username });
                }}
                size="small"
                disabled={isRoleViewOnly}
              >
                {t('table.permissionsButton')}
              </Button>
              <Button
                id={`user-reset-password-${data.Username}`}
                onClick={() => handleResetPasswordDialog(data.Username)}
                size="small"
                disabled={isRoleViewOnly}
              >
                {t('table.resetPasswordButton')}
              </Button>
              <Button
                id={`user-delete-${data.Username}`}
                onClick={() => {
                  handlers.onDeleteUser({ EmailAddress: data.Username });
                }}
                size="small"
                disabled={isRoleViewOnly}
              >
                {t('table.removeButton')}
              </Button>
            </Box>
          );
        },
      },
    ],
    rows: accounts.map((user) => ({
      id: String(user.Username),
      data: user,
    })),
  };

  if (accounts.length === 0) {
    return (
      <div className={cn('emptyState')}>
        <img src={PersonAddIcon} className={cn('emptyIcon')} alt="" />
        <Box flx="col" ai="center" gapSize="10px">
          <Title level="4">{t('emptyState.title', { defaultValue: 'Add Portal Users' })}</Title>
          <p className={cn('emptyDescription')}>
            {t('emptyState.description', { defaultValue: 'Click Add User to add account portal users.' })}
          </p>
        </Box>
        <Button
          id="add-user-empty-state"
          onClick={handlers.onAddUserPage}
          colorScheme="primary"
          disabled={isRoleViewOnly}
          size="standard"
        >
          {t('addUserButton')}
        </Button>
      </div>
    );
  }

  return (
    <Box flx="col" gapSize="16px">
      <Box jc="fe" ai="center">
        <Button
          id="showHideAccountCredentials"
          onClick={handlers.onAddUserPage}
          colorScheme="primary"
          disabled={isRoleViewOnly}
          size="small"
        >
          {t('addUserButton')}
        </Button>
      </Box>

      <Table {...tableProps} />
    </Box>
  );
};

export default Users;
